import React from "react";
import { motion } from "framer-motion";
import { Shield, Cookie, ScrollText } from "lucide-react";
import { SectionHeading } from "../components/shared/SectionHeading";

const ICONS = {
    privacy: Shield,
    cookies: Cookie,
    terms: ScrollText,
};

const COPY = {
    privacy: {
        eyebrow: "Privacy Policy",
        title: <>Your data, <span className="text-gradient-brand">handled with discipline.</span></>,
        subtitle:
            "How Netfotech Solutions collects, uses, retains and protects information when you interact with our website, communications and services.",
        sections: [
            {
                heading: "Information we collect",
                body: "We collect information you provide directly such as your name, business email, phone number and message when you submit our contact form, subscribe to our newsletter or apply for a role. We also collect technical data via cookies and analytics events (page views, referrers, device type) to improve site performance.",
            },
            {
                heading: "How we use your information",
                body: "Your information is used to respond to enquiries, deliver the resources you request, evaluate job applications, operate and improve our website, and where you have opted in share occasional product updates and insights. We do not sell or rent personal data to third parties.",
            },
            {
                heading: "Data retention",
                body: "We retain enquiry and application data only for as long as necessary to fulfil the purpose for which it was collected, comply with legal obligations, resolve disputes and enforce our agreements.",
            },
            {
                heading: "Your rights",
                body: "You may request access, correction or deletion of your personal data at any time by writing to privacy@netfotech.in. Where applicable under GDPR or India DPDP Act, we will respond within statutory timelines.",
            },
            {
                heading: "Security",
                body: "We apply layered technical and organisational safeguards encryption in transit, principle of least privilege, and routine third-party assessments to protect data we hold. No method is 100% secure, but we treat your information with the same diligence we apply to client systems.",
            },
            {
                heading: "Updates to this policy",
                body: "We may revise this policy to reflect operational, legal or regulatory changes. The latest version will always be published on this page with the effective date.",
            },
        ],
    },
    cookies: {
        eyebrow: "Cookie Policy",
        title: <>Small files, <span className="text-gradient-brand">transparent intent.</span></>,
        subtitle:
            "How and why we use cookies and similar technologies on netfotech.in and the choices you have.",
        sections: [
            {
                heading: "What are cookies",
                body: "Cookies are small text files placed on your device when you visit a website. They help the site recognise you, remember preferences and measure usage. We use first-party cookies and a small number of third-party cookies for analytics.",
            },
            {
                heading: "Categories we use",
                body: "Strictly necessary (site functionality, form integrity), Analytics (anonymous page views, referrer attribution to improve content) and Preferences (theme, language). We do not use advertising cookies.",
            },
            {
                heading: "Managing cookies",
                body: "You can clear or block cookies anytime via your browser settings. Disabling strictly necessary cookies may impact form submissions or page rendering. Disabling analytics cookies will not affect your experience but limits our ability to improve the site.",
            },
            {
                heading: "Third-party services",
                body: "Selected pages embed third-party widgets (e.g., video, analytics). These providers may set their own cookies governed by their privacy notices.",
            },
        ],
    },
    terms: {
        eyebrow: "Terms & Conditions",
        title: <>Clear ground rules <span className="text-gradient-brand">for engagement.</span></>,
        subtitle:
            "By accessing or using netfotech.in you agree to the following terms. Please read them carefully.",
        sections: [
            {
                heading: "Use of the website",
                body: "Content on this site is provided for general information about Netfotech Solutions and does not constitute professional advice. You agree to use the site lawfully and not to interfere with its operation, security or other users' access.",
            },
            {
                heading: "Intellectual property",
                body: "All content including text, graphics, logos, video, code samples and case studies is owned by or licensed to Netfotech Solutions. You may view and share material with attribution; you may not reproduce, modify, distribute or commercialise it without prior written consent.",
            },
            {
                heading: "Submissions",
                body: "Information you voluntarily submit (contact forms, applications, comments) is governed by our Privacy Policy. You confirm that submissions are accurate and that you have the right to share them.",
            },
            {
                heading: "Disclaimer",
                body: "We strive for accuracy but make no warranty that the website is error-free, uninterrupted, or that results obtained from its use will meet your requirements. To the fullest extent permitted by law, Netfotech Solutions is not liable for indirect or consequential losses.",
            },
            {
                heading: "Governing law",
                body: "These terms are governed by the laws of India. Any disputes will be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.",
            },
            {
                heading: "Contact",
                body: "Questions about these terms? Write to legal@netfotech.in.",
            },
        ],
    },
};

export default function Legal({ kind }) {
    const data = COPY[kind] || COPY.privacy;
    const Icon = ICONS[kind] || Shield;
    return (
        <main data-testid={`page-legal-${kind}`} className="relative pt-32 md:pt-40">
            <section className="relative pb-12">
                <div className="absolute inset-0 -z-10 bg-radial-fade" />
                <div className="max-w-[900px] mx-auto px-5 md:px-8 lg:px-10">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl glass-strong border border-white/15 mb-6">
                        <Icon className="h-5 w-5 text-brand-green" />
                    </div>
                    <SectionHeading
                        eyebrow={data.eyebrow}
                        title={data.title}
                        subtitle={data.subtitle}
                    />
                    <p className="mt-6 text-[12.5px] text-zinc-500 uppercase tracking-[0.18em]">
                        Effective: 1 January 2026
                    </p>
                </div>
            </section>

            <section className="relative pb-32">
                <div className="max-w-[900px] mx-auto px-5 md:px-8 lg:px-10 space-y-7">
                    {data.sections.map((s, i) => (
                        <motion.article
                            key={s.heading}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, delay: i * 0.04 }}
                            data-testid={`legal-${kind}-section-${i}`}
                            className="glass border border-white/10 rounded-2xl p-7 md:p-8"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <span className="font-mono text-[11px] text-brand-green">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <h2 className="font-display text-lg md:text-xl font-semibold tracking-tight">
                                    {s.heading}
                                </h2>
                            </div>
                            <p className="text-[14.5px] text-zinc-400 leading-relaxed">
                                {s.body}
                            </p>
                        </motion.article>
                    ))}
                </div>
            </section>
        </main>
    );
}
